import React, { useState, useEffect, useRef } from 'react';
import './InteractiveList.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Filter from '../Filter/Filter';
import lowerCaseFirstLetter from '../../common/lowerCaseFirstLetter';
import logComponent from '../../common/logComponent';

export default function InteractiveList({ children, items, searchTextFields, defaultSortField, columns, singularItemTerm, pluralItemTerm, itemsLoading, itemsError, rowComponent: Row, showDetail, searchPlaceholder, keyField, reloadAction, filters: propsFilters }) {
  const [displayItems, setDisplayItems] = useState(items != null ? Array.from(items) : null);
  const [sortField, setSortField] = useState(defaultSortField != null ? defaultSortField : null);
  const [sortDirection, setSortDirection] = useState('ascending');
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState([]);
  const isUpdatingList = useRef(false);

  if (logComponent('InteractiveList')) { console.debug('InteractiveList rendered'); }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (logComponent('InteractiveList')) { console.debug('InteractiveList onMount'); }
    updateList();
  }, [])

  useEffect(() => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList useEffect [items]'); }
    if(items)
      updateList();
  }, [items])

  useEffect(() => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList useEffect [sortField, sortDirection, searchText, filters]'); }
    updateList();
  }, [sortField, sortDirection, searchText, filters])

  const setUpdatingStatus = (el) => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList setUpdatingStatus'); }
    isUpdatingList.current = el;
  }

  const updateSort = (e) => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList updateSort'); }
    const { sortField: newSortField } = e.currentTarget.dataset;


    if (!newSortField) return;

    if (newSortField === sortField) {
      toggleSortDirection();
    } else {
      updateSortField(newSortField);
    }
  }

  const toggleSortDirection = () => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList toggleSortDirection'); }
    const newDirection = (sortDirection === 'ascending' ? 'descending' : 'ascending');
    setSortDirection(newDirection);
  }

  const updateSearchText = (e) => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList updateSearchText: ' + e); }
    setSearchText(e.target.value);
  }

  const updateSortField = (field) => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList updateSortField' + field); }
    setSortField(field);
    setSortDirection('ascending');
  }

  const updateList = () => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList updateList'); }
    if (isUpdatingList.current) return;
    setUpdatingStatus(true);
    // Prevent search until items have been loaded
    if (!items) {
        setUpdatingStatus(false);
        return;
    }

    let itemsToDisplay = Array.from(items);
    
    // filter by searchText
    if (searchText) {
      let searchTextSegments = searchText.split(/ +/).filter(segment => segment !== '');

      itemsToDisplay = itemsToDisplay
      .filter(item => {
        return searchTextFields.some(field => {
          let camelCaseField = lowerCaseFirstLetter(field);
          return item[camelCaseField] && 
            searchTextSegments.some(segment => item[camelCaseField].toLowerCase().includes(segment.toLowerCase()));
        }); 
      });
    }
  
    // this is functioning as an AND right now; an item has to match
    // all filters in order to make it through clickable filters
    if (filters.length > 0) {
      itemsToDisplay = itemsToDisplay.filter(item => {
        return filters.every(filter => {
          return filter.criteria.some(criterion => {
            const parsedFilterField = parseSortField(criterion.field);

            if (!parsedFilterField) return false;

            return criterion.acceptedValues.includes(getNestedField(item, parsedFilterField));
          });
        })
      })
    }

    // sort by sortField and sortDirection
    const parsedSortFields = parseSortField(sortField);
   
    if (parsedSortFields) {
      itemsToDisplay.sort((a, b) => {
        let val = 0;

        let itemA = getNestedField(a, parsedSortFields)?.toLowerCase();
        let itemB = getNestedField(b, parsedSortFields)?.toLowerCase();

        if (sortDirection === 'ascending') {
          val = itemA < itemB ? -1 : 1;
        } else if (sortDirection === 'descending') {
          val = itemA < itemB ? 1 : -1;
        }

        return val;
      });
    } 
    
    setDisplayItems(itemsToDisplay);
    setUpdatingStatus(false);
  }

  const parseSortField = (thisSortField) => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList parseSortField:' + thisSortField); }
    if (!thisSortField) return null;

    return thisSortField.split('.');
  }

  const getNestedField = (obj, fields) => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList getNestedField'); }
    return fields.reduce((prev, curr) => {
      return prev[lowerCaseFirstLetter(curr)];
    }, obj);
  }

  const sort = () => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList sort'); }
    const itemsToSort = Array.from(displayItems);

    itemsToSort.sort((a, b) => {
      let val = 0;

      if (sortDirection === 'ascending') {
        val = a[sortField] < b[sortField] ? -1 : 1;
      } else if (sortDirection === 'descending') {
        val = a[sortField] < b[sortField] ? 1 : -1;
      }

      return val;
    });
    setDisplayItems(itemsToSort);

  }

  const updateFilters = (newFilter) => {
    if (logComponent('InteractiveList')) { console.debug('InteractiveList newFilter'); }
    let newFilters = filters.filter(oldFilter => newFilter.name !== oldFilter.name);
    if (newFilter.criteria.length > 0) {
      newFilters = newFilters.concat(newFilter);
    }
    setFilters(newFilters);
  }


    return (
      <div className="Interactive-List">
        <div className="Interactive-List__filter-row">
          <div className="Interactive-List__search-input-wrapper">
            <FontAwesomeIcon icon="search" className="Interactive-List__search-icon" />
            <input 
              type="text" 
              className="Interactive-List__search-input"
              placeholder={searchPlaceholder}
              value={searchText}
              onChange={updateSearchText}/>
          </div>
        {
            propsFilters && 
          <div className="Interactive-List__filter-wrapper">
            <Filter 
              filters={propsFilters}
              onChange={updateFilters} />
          </div>
        }
        </div>

        { children }

        <table className="Interactive-List__table">
          <thead className="Interactive-List__thead">
            <tr>
            {
              columns.map(column => 
                {
                  return (
                  <th 
                  key={ column.name }
                  className={'Interactive-List__heading' + 
                              (column.hideAt ? ' hide-at-' + column.hideAt : '') + 
                              (sortField === column.sortField ? ' Interactive-List__heading--selected' : '')}
                  style={ column.style }>
                  {
                    column.sortField ?
                    <button 
                      className="Interactive-List__sort-btn"
                      data-sort-field={ column.sortField }
                      onClick={ updateSort }  
                    >
                      { column.name }
                        <FontAwesomeIcon 
                          icon={(sortDirection === 'ascending' ? 'arrow-up' : 'arrow-down')}
                          className={'Interactive-List__sort-icon' + (sortField === column.sortField ? ' Interactive-List__sort-icon--visible' : '') } /> 
                    </button> :
                    <span>{ column.name }</span>
                  }
                </th>)
                }
              )
            }
            </tr>
          </thead>
          <tbody className="Interactive-List__tbody">
          {
            !itemsLoading && !itemsError && displayItems && displayItems.length > 0 &&
            displayItems.map((item, index) => {
              let itemProp = {};
              itemProp[singularItemTerm] = item;

              return (<Row 
                {...itemProp}
                key={item[keyField]} // index
                showDetail={showDetail}
                // TODO:  add a remove prop as well (needed in ProcessChanges)
              />);
              }
            )
          }
          </tbody>
        </table>
        {
          !itemsLoading && !itemsError && displayItems && displayItems.length === 0 &&
          <p className="Interactive-List__empty">No { pluralItemTerm } found...</p>
        }
        {
          itemsLoading &&
          <p className="Interactive-List__loading">Loading { pluralItemTerm }...</p>
        }
        {
          itemsError && 
          <div className="Interactive-List__error">
            <p className="Interactive-List__error-text">There was an error loading the { pluralItemTerm }.</p>
            \<button 
              className="Interactive-List__reload"
              onClick={reloadAction}>
              Try Again
            </button>
          </div>
        }
      </div>
    );
}